import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { format, addDays, addMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { X, Plus, Trash2 } from 'lucide-react';
import { agentAppAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DtdAfter8pmNotice from '../../components/common/DtdAfter8pmNotice';
import LongTermRegularNotice from '../../components/common/LongTermRegularNotice';
import { useLongTermAutoStart, longTermRegularNoticeApplies } from '../../utils/longTermNotice';
import { dtdStartsTomorrowFrom } from '../../utils/dtdCutoff';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const toInput = (d) => d ? format(new Date(d), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

// Same fixed set the admin desktop's Renew tab offers (CustomerDetailPage.js PERIOD_OPTIONS) — DTD customers pick a validity in days.
const PERIOD_OPTIONS = [30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 720].map((d) => ({
  label: `${d} Days`, days: d,
}));

// Regular (calendar-month) customers instead pick a number of months to cover in one go —
// paymentService.collectPayment always starts the period on the 1st of the current month
// and extends periodTo by this many month-ends (periodMonths), so there's no "days" concept
// to show them at all.
const MONTH_OPTIONS = [1, 2, 3, 6, 12].map((m) => ({ label: `${m} Month${m > 1 ? 's' : ''}`, months: m }));

export default function AgentCollectPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setHeader } = useOutletContext();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  // Expected bill total = old due + this renewal's charge (or the custom Edit Bill total).
  // Tracked separately from `amount` so a manually edited amount compares against it —
  // the live New Balance below and paymentService's billTotal field both use it.
  const [billTotal, setBillTotal] = useState(0);
  const [discount, setDiscount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [sendMessage, setSendMessage] = useState(true);
  const [paymentDate, setPaymentDate] = useState(() => format(new Date(), "yyyy-MM-dd'T'HH:mm")); // local (IST) time — toISOString() here was UTC, 5.5 h behind
  const [saving, setSaving] = useState(false);

  // Renew period — DTD customers pick an explicit From/To range; regular customers just
  // pick how many calendar months to cover (see MONTH_OPTIONS above).
  const [periodDays, setPeriodDays] = useState(30);
  const [periodMonths, setPeriodMonths] = useState(1);
  const [fromDate, setFromDate] = useState(toInput(new Date()));
  // Expiry ("Date To") defaults to whatever the period options compute, but can be directly
  // overridden — cleared back to null (falls back to the computed value) whenever the start
  // date or period selection changes, so switching options doesn't leave a stale override behind.
  const [toDateOverride, setToDateOverride] = useState(null);
  const [show8pmNotice, setShow8pmNotice] = useState(false);
  const [showLongTermNotice, setShowLongTermNotice] = useState(false);
  const longTermAuto = useLongTermAutoStart();

  // Custom bill line items (bix42 "Edit Bill") — null means bill just the plain package line.
  const [billItems, setBillItems] = useState(null);
  const [editBillOpen, setEditBillOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const perms = user?.permissions || {};
  const canAdjust = isAdmin || perms.canAdjustBalance;
  const canBackdate = isAdmin || perms.addPaymentOnAnyDate;

  const load = useCallback(async () => {
    try {
      const { data } = await agentAppAPI.getCustomer(id);
      const c = data.data;
      setCustomer(c);
      const additionalTotal = (c.catv?.additionalPackages || []).reduce((s, a) => s + (a.price || 0), 0);
      // Prefill = old due + one period's charge (user rule: renewing always collects the
      // previous balance along with the renewal amount, e.g. due 0 + 3 months 1200 = 1200).
      const monthly = c.catv?.package?.price ? c.catv.package.price + additionalTotal : 0;

      const validityDays = c.catv?.package?.validityDays;
      const days = PERIOD_OPTIONS.some((o) => o.days === validityDays) ? validityDays : 30;
      setPeriodDays(days);

      // DTD customers' charge scales with the picked days (same ×days/30 rule as the admin
      // Renew tab); regular customers' charge is per calendar month.
      const isReg = (c.billing?.durationType || 'regular') === 'regular';
      const charge = isReg ? monthly : Math.round(monthly * days / 30);
      const bill = (c.outstandingBalance || 0) + charge;
      setBillTotal(bill);
      setAmount(monthly || c.outstandingBalance ? String(bill) : '');

      // Start date is always the day AFTER the current expiry — an expired customer's
      // renewal covers the lapsed months since expiry, not from today.
      const exp = c.catv?.expiryDate ? new Date(c.catv.expiryDate) : null;
      setFromDate(toInput(exp ? addDays(exp, 1) : new Date()));
      setToDateOverride(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customer');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setHeader({ title: 'Renew', showBack: true }); }, [setHeader]);

  if (!customer) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;

  const isRegular = (customer.billing?.durationType || 'regular') === 'regular';
  const toDate = toDateOverride ?? (fromDate ? format(addDays(new Date(fromDate), periodDays - 1), 'yyyy-MM-dd') : '');
  // Mirrors paymentService.collectPayment's regular-customer date math exactly: start + N
  // months - 1 day (equivalent to "end of month" when start is the 1st, but works for any
  // agent-picked start date too).
  const toDateRegular = toDateOverride ?? (fromDate ? format(addDays(addMonths(new Date(fromDate), periodMonths), -1), 'yyyy-MM-dd') : '');
  // Discount is a write-off on top of the amount collected — both reduce the outstanding
  // balance independently (paymentService.collectPayment's additiveDiscount mode).
  // New Balance = expected bill − collected: positive = due left, negative = advance.
  // paymentService lands the balance exactly here via the billTotal payload field.
  const newBalance = billTotal - Number(amount || 0) - Number(discount || 0);
  const packageName = customer.catv?.package?.name;
  const additionalPackages = customer.catv?.additionalPackages || [];
  const additionalTotal = additionalPackages.reduce((s, a) => s + (a.price || 0), 0);

  // Every path lands on the receipt/view page first (bix42's flow: generate, then view the
  // bill, then collect against it there if it's still unpaid) — there's no "stay here" path
  // anymore, so Generate and Generate & Collect only differ in the `collected` flag they send.
  const submit = async (collected, { confirmed8pm = false, confirmedLongTerm = false } = {}) => {
    if (!amount || Number(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    // After-8 PM DTD CableTV rule (utils/dtdCutoff.js) — the agent confirms before collecting.
    const collectAt = canBackdate ? new Date(paymentDate) : new Date();
    if (collected && customer.billing?.durationType === 'dtd' && !confirmed8pm && dtdStartsTomorrowFrom(fromDate, collectAt)) {
      setShow8pmNotice(true);
      return;
    }
    // 6/12 months for a regular customer with the automatic box swap on (utils/longTermNotice.js).
    if (collected && isRegular && longTermRegularNoticeApplies(longTermAuto, 'regular', periodMonths) && !confirmedLongTerm) {
      setShowLongTermNotice(true);
      return;
    }
    setShow8pmNotice(false);
    setShowLongTermNotice(false);
    setSaving(true);
    try {
      const payload = {
        amount: Number(amount),
        discount: Number(discount) || 0,
        paymentMethod,
        notes,
        collected,
        items: billItems || undefined,
        sendMessage,
        // Expected bill total — the server lands the balance on billTotal − collected
        // (due left / advance), matching the New Balance shown on this screen.
        billTotal,
      };
      if (canBackdate) payload.paymentDate = new Date(paymentDate).toISOString();
      if (isRegular) {
        payload.periodMonths = periodMonths;
        payload.periodFrom = fromDate;
        payload.periodTo = toDateRegular;
      } else {
        payload.periodFrom = fromDate;
        payload.periodTo = toDate;
      }

      const { data } = await agentAppAPI.collectPayment(id, payload);
      toast.success(data.message || (collected ? 'Payment recorded' : 'Bill generated (unpaid)'));
      navigate(`/agent/receipt/${data.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {[
          { label: 'Customer', value: customer.name },
          { label: 'Balance', value: `₹${fmt(customer.outstandingBalance)}` },
          { label: 'Package', value: packageName ? `${packageName} @${customer.catv.package.price}${additionalPackages.length ? ` +${additionalPackages.length}` : ''}` : '—' },
        ].map(({ label, value }, i) => (
          <div key={label} style={{ padding: '8px 6px', textAlign: 'center', borderRight: i < 2 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: '.8rem' }}>{value}</div>
          </div>
        ))}
      </div>

      {additionalPackages.length > 0 && (
        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: 10 }}>
          + {additionalPackages.map((a) => `${a.package?.name || 'add-on'} (₹${a.price})`).join(', ')}
        </div>
      )}

      {customer.catv?.package?.pricingTiers?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Quick Fill</label>
          <select className="form-control" defaultValue=""
            onChange={(e) => {
              const tier = customer.catv.package.pricingTiers.find((t) => t.label === e.target.value);
              if (!tier) return;
              setToDateOverride(null);
              if (!billItems) {
                const bill = (customer.outstandingBalance || 0) + tier.price + additionalTotal;
                setBillTotal(bill);
                setAmount(String(bill));
              }
              if (isRegular) setPeriodMonths(tier.months);
              else setPeriodDays(tier.months * 30);
            }}>
            <option value="">Custom (set below)</option>
            {customer.catv.package.pricingTiers.map((t) => (
              <option key={t.label} value={t.label}>{t.label} — ₹{t.price}</option>
            ))}
          </select>
        </div>
      )}

      {isRegular ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Start Date</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="date" className="form-control" style={{ flex: 1 }} value={fromDate} onChange={(e) => { setFromDate(e.target.value); setToDateOverride(null); }} />
              <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>To</span>
              <input type="date" className="form-control" style={{ flex: 1 }} value={toDateRegular} onChange={(e) => setToDateOverride(e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Period</label>
            <select className="form-control" value={periodMonths} onChange={(e) => {
              const months = Number(e.target.value);
              setPeriodMonths(months);
              setToDateOverride(null);
              // Scale the amount to cover the newly-picked number of months, on top of the
              // old due (old amount + renew amount, e.g. due 0 + 3×400 = 1200).
              const price = customer.catv?.package?.price;
              if (!billItems && price) {
                const bill = (customer.outstandingBalance || 0) + (price + additionalTotal) * months;
                setBillTotal(bill);
                setAmount(String(bill));
              }
            }}>
              {MONTH_OPTIONS.map((o) => <option key={o.months} value={o.months}>{o.label}</option>)}
            </select>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Regular billing — covers {periodMonths} month{periodMonths > 1 ? 's' : ''} from {fromDate ? format(new Date(fromDate), 'do MMM yyyy') : '—'} through {toDateRegular ? format(new Date(toDateRegular), 'do MMM yyyy') : '—'}.
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Date</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="date" className="form-control" style={{ flex: 1 }} value={fromDate} onChange={(e) => { setFromDate(e.target.value); setToDateOverride(null); }} />
              <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>To</span>
              <input type="date" className="form-control" style={{ flex: 1 }} value={toDate} onChange={(e) => setToDateOverride(e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Period</label>
            <select className="form-control" value={periodDays} onChange={(e) => {
              const days = Number(e.target.value);
              setPeriodDays(days);
              setToDateOverride(null);
              // Scale the amount to the picked validity, on top of the old due — same
              // ×days/30 charge rule as the admin Renew tab (e.g. due 0 + 60 days of ₹400 = 800).
              const price = customer.catv?.package?.price;
              if (!billItems && price) {
                const bill = (customer.outstandingBalance || 0) + Math.round((price + additionalTotal) * days / 30);
                setBillTotal(bill);
                setAmount(String(bill));
              }
            }}>
              {PERIOD_OPTIONS.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
            </select>
          </div>
        </>
      )}

      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: '.8rem', color: 'var(--text-muted)', display: 'block' }}>Amount (in ₹)</label>
        <input
          type="number" value={amount} disabled={!!billItems}
          onChange={(e) => setAmount(e.target.value)}
          style={{ fontSize: '1.8rem', fontWeight: 700, textAlign: 'center', border: 'none', borderBottom: '2px solid var(--border)', width: 160, background: 'transparent', color: '#dc2626' }}
        />
      </div>

      {billItems && (
        <div style={{ fontSize: '.75rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 14, textAlign: 'center' }}>
          Custom bill: {billItems.length} item{billItems.length !== 1 ? 's' : ''}.{' '}
          <button onClick={() => setEditBillOpen(true)} style={{ background: 'none', border: 'none', color: '#b45309', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Edit</button>
          {' · '}
          <button onClick={() => {
            setBillItems(null);
            // Back to the standard bill: old due + the currently-picked period's charge.
            const price = customer.catv?.package?.price || 0;
            const charge = isRegular ? (price + additionalTotal) * periodMonths : Math.round((price + additionalTotal) * periodDays / 30);
            const bill = (customer.outstandingBalance || 0) + charge;
            setBillTotal(bill);
            setAmount(String(bill));
          }} style={{ background: 'none', border: 'none', color: '#b45309', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Clear</button>
        </div>
      )}

      <Row label="Discount (in ₹)">
        <input
          type="number" value={discount} disabled={!canAdjust}
          onChange={(e) => setDiscount(e.target.value)}
          className="form-control" style={{ width: 100, textAlign: 'right' }}
        />
      </Row>
      <Row label="New Balance" bold>
        {newBalance > 0 && <span style={{ color: '#dc2626' }}>Due left ₹{fmt(newBalance)}</span>}
        {newBalance < 0 && <span style={{ color: '#16a34a' }}>Advance ₹{fmt(-newBalance)}</span>}
        {newBalance === 0 && <span style={{ color: '#16a34a' }}>₹0 — fully settled</span>}
      </Row>

      <div style={{ display: 'flex', gap: 10, margin: '14px 0' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Record Time</label>
          <input
            type="datetime-local" value={paymentDate} disabled={!canBackdate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="form-control"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mode</label>
          <select className="form-control" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="payment_gateway">Payment Gateway</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="coupon">Coupon</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Comment</label>
        <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-card)' }}
        onClick={() => setSendMessage((v) => !v)} role="switch" aria-checked={sendMessage}>
        <div style={{ userSelect: 'none' }}>
          <div style={{ fontSize: '.85rem', fontWeight: 600 }}>Send message to customer</div>
          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>
            {sendMessage ? 'WhatsApp bill/receipt will be sent' : 'No message for this renewal'}
          </div>
        </div>
        <span style={{
          width: 42, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
          background: sendMessage ? '#16a34a' : '#d1d5db', transition: 'background .15s',
        }}>
          <span style={{
            position: 'absolute', top: 2, left: sendMessage ? 20 : 2, width: 20, height: 20,
            borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          }} />
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {show8pmNotice && (
          <DtdAfter8pmNotice
            audience="staff"
            at={canBackdate ? new Date(paymentDate) : new Date()}
            onConfirm={() => submit(true, { confirmed8pm: true })}
            onCancel={() => setShow8pmNotice(false)}
          />
        )}
        {showLongTermNotice && (
          <LongTermRegularNotice
            audience="staff"
            months={periodMonths}
            at={canBackdate ? new Date(paymentDate) : new Date()}
            onConfirm={() => submit(true, { confirmedLongTerm: true })}
            onCancel={() => setShowLongTermNotice(false)}
          />
        )}
        <ActionButton onClick={() => submit(true)} disabled={saving} style={{ background: 'var(--bg-sidebar)' }}>
          {saving ? 'Working…' : 'Generate & Collect Payment'}
        </ActionButton>
        <ActionButton onClick={() => submit(false)} disabled={saving} style={{ background: '#fff', color: '#b45309', border: '1.5px solid #f59e0b' }}
          title="Raise the bill without collecting payment — adds to outstanding balance, opens it to view/share/collect later">
          {saving ? 'Working…' : 'Generate'}
        </ActionButton>
        <ActionButton onClick={() => setEditBillOpen(true)} disabled={saving} style={{ background: '#fff', color: 'var(--primary)', border: '1.5px solid #a78bfa' }}>
          Edit Bill
        </ActionButton>
      </div>

      {editBillOpen && (
        <EditBillModal
          initialItems={billItems || [{ label: packageName || 'Subscription', amount: parseFloat(amount) || customer.catv?.package?.price || 0 }]}
          onClose={() => setEditBillOpen(false)}
          onSave={(items) => {
            setBillItems(items);
            const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
            setBillTotal(total);
            setAmount(String(total));
            setEditBillOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Row({ label, children, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: '.85rem', fontWeight: bold ? 700 : 500 }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 500 }}>{children}</span>
    </div>
  );
}

function ActionButton({ onClick, disabled, children, style, title }) {
  return (
    <button
      onClick={onClick} disabled={disabled} title={title}
      style={{
        color: '#fff', border: 'none', borderRadius: 24,
        padding: '12px 0', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function EditBillModal({ initialItems, onClose, onSave }) {
  const [items, setItems] = useState(initialItems.map((i) => ({ label: i.label, amount: String(i.amount ?? '') })));
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const setItem = (idx, k, v) => setItems((list) => list.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));

  const save = () => {
    const clean = items
      .map((i) => ({ label: i.label.trim(), amount: parseFloat(i.amount) || 0 }))
      .filter((i) => i.label);
    if (!clean.length) { toast.error('Add at least one item'); return; }
    if (clean.reduce((s, i) => s + i.amount, 0) <= 0) { toast.error('Bill total must be more than 0'); return; }
    onSave(clean);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'flex-end', justifyContent: 'center', zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontWeight: 700 }}>Edit Bill</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
          These line items appear on the printed bill. The bill amount becomes their total.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 30px', gap: 8, alignItems: 'center' }}>
              <input className="form-control" placeholder="Item description" value={it.label} onChange={(e) => setItem(i, 'label', e.target.value)} />
              <input className="form-control" type="number" placeholder="₹" value={it.amount} onChange={(e) => setItem(i, 'amount', e.target.value)} />
              <button onClick={() => setItems((list) => list.filter((_, x) => x !== i))} title="Remove item"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', justifyContent: 'center' }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        <button className="form-control" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 14, cursor: 'pointer' }}
          onClick={() => setItems((list) => [...list, { label: '', amount: '' }])}>
          <Plus size={13} /> Add Item
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontWeight: 700 }}>Total: ₹{fmt(total)}</div>
          <button onClick={save} style={{ background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, cursor: 'pointer' }}>
            Save Items
          </button>
        </div>
      </div>
    </div>
  );
}
